import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, UserPlus, Pencil, Pause, Play, Trash2 } from "lucide-react";
import { EmployeeDialog } from "./EmployeeDialog";
import { InviteDialog } from "./InviteDialog";
import { deptName } from "./useOrg";

function EmployeeRow({ e, data, onEdit, onRemove, reload }) {
  const act = async (path) => {
    try { await api.post(`/org/employees/${e.id}/${path}`); toast.success("Done"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const { user } = useAuth();
  const editable = user?.role === "owner" || e.editable;
  const roles = (e.assignments || []).map((a) => `${a.role} · ${deptName(data, a.department)}`).join(", ") || "No role";
  const branch = data.branches.find((b) => b.id === e.branch_id)?.name || "Head office";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`employee-row-${e.id}`}>
      <div className="min-w-0">
        <p className="font-heading font-semibold text-stone-900">{e.name} {e.has_org_access && <span className="ml-1 rounded-full bg-[#F0ECE1] px-2 py-0.5 text-[10px] font-bold uppercase text-[#0D5C3A]">Org access</span>}</p>
        <p className="text-sm text-stone-500">{roles} · {branch}</p>
        <p className="font-mono text-xs text-stone-400">{e.mobile}{e.email ? ` · ${e.email}` : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={e.status} />
        {editable && <>
        <Button variant="outline" size="sm" onClick={() => onEdit(e)} data-testid={`employee-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
        {e.status === "active"
          ? <Button variant="outline" size="sm" onClick={() => act("suspend")} data-testid={`employee-suspend-${e.id}`}><Pause className="h-4 w-4" /></Button>
          : <Button variant="outline" size="sm" onClick={() => act("reactivate")} data-testid={`employee-reactivate-${e.id}`}><Play className="h-4 w-4" /></Button>}
        <Button variant="outline" size="sm" className="text-rose-600" onClick={() => onRemove(e)} data-testid={`employee-remove-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
        </>}
      </div>
    </div>
  );
}

export function EmployeesTab({ data, reload, compact }) {
  const { business } = useAuth();
  const [invite, setInvite] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const openNew = () => { setEditing(null); setOpen(true); };
  const remove = async () => {
    try { await api.delete(`/org/employees/${removing.id}`); toast.success("Employee removed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setRemoving(null);
  };
  return (
    <div className="space-y-4" data-testid="employees-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-500">{data.employees.length} employee{data.employees.length === 1 ? "" : "s"} · internal team only, separate from business partners.</p>
        <Button onClick={openNew} data-testid="add-employee-btn" className="bg-[#0D5C3A] hover:bg-[#084229]"><UserPlus className="mr-1 h-4 w-4" /> Add employee</Button>
      </div>
      {data.employees.length === 0 ? (
        <EmptyState icon={Users} title="No employees yet" description="Add your team so they can sign in with their own mobile number and password." testid="employees-empty" />
      ) : (
        <div className={`divide-y divide-[#F0ECE1] rounded-2xl border border-[#E5E0D8] bg-white px-5 ${compact ? "max-h-80 overflow-y-auto" : ""}`}>
          {data.employees.map((e) => <EmployeeRow key={e.id} e={e} data={data} reload={reload} onEdit={(x) => { setEditing(x); setOpen(true); }} onRemove={setRemoving} />)}
        </div>
      )}
      <EmployeeDialog open={open} onOpenChange={setOpen} data={data} employee={editing} onSaved={reload} onInvite={setInvite} />
      <InviteDialog invite={invite} bizName={business?.name} onClose={() => setInvite(null)} />
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {removing?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They will no longer be able to sign in. Order history is preserved.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-remove-employee">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
